import { type ReactElement, type ReactNode } from 'react'
import { Link } from 'react-router'
import { MediaImage } from '#app/components/image.tsx'
import {
	BackgroundGradient,
	PayloadFooter,
	PayloadHeader,
} from '#app/routes/_marketing+/index.tsx'
import { cn } from '#app/utils/misc.tsx'

export type MarketingFeature = {
	description: string
	label?: string
	title: string
}

export type MarketingStat = {
	label: string
	value: string
}

export type MarketingHeroSource = {
	href: string
	label: string
}

export function MarketingPageShell({ children }: { children: ReactNode }) {
	return (
		<div className="dpe-live-page dpe-live-page--light">
			<BackgroundGradient />
			<PayloadHeader />
			<main>{children}</main>
			<PayloadFooter />
		</div>
	)
}

export function MarketingHero({
	actions,
	eyebrow,
	imageAlt,
	imageSrc,
	media,
	sources,
	stats,
	subtitle,
	title,
}: {
	actions?: ReactNode
	eyebrow: string
	imageAlt?: string
	imageSrc?: string
	media?: ReactNode
	sources?: MarketingHeroSource[]
	stats?: MarketingStat[]
	subtitle: string
	title: string
}) {
	return (
		<section className="relative overflow-hidden border-b border-black/10">
			<GridBackdrop />
			<div className="mx-auto grid min-h-[560px] max-w-[1440px] grid-cols-1 px-5 py-16 sm:px-8 lg:grid-cols-[0.96fr_1.04fr] lg:px-10 lg:py-24">
				<div className="relative z-10 flex flex-col justify-center border-black/10 lg:border-r lg:pr-14">
					<span className="inline-block self-start border border-black bg-[#EC1C23] px-3 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white">
						{eyebrow}
					</span>
					<h1 className="mt-7 max-w-[760px] text-[clamp(3rem,7vw,6.8rem)] font-medium leading-[0.92] tracking-[-0.03em]">
						{title}
					</h1>
					<p className="text-black/66 mt-8 max-w-2xl text-lg leading-8">
						{subtitle}
					</p>
					{actions ? (
						<div className="dpe-live-hero-buttons mt-10">{actions}</div>
					) : null}
				</div>
				<div className="relative mt-12 flex items-center lg:mt-0 lg:pl-14">
					<div className="relative w-full border border-black bg-[#fffffa] p-3 shadow-[0_30px_90px_-70px_rgba(16,16,16,0.7)]">
						<div className="pointer-events-none absolute -left-3 -top-3 h-6 w-6 border-l border-t border-black/45" />
						<div className="pointer-events-none absolute -bottom-3 -right-3 h-6 w-6 border-b border-r border-black/45" />
						{media ? (
							media
						) : imageSrc ? (
							<MediaImage
								alt={imageAlt ?? ''}
								aspectRatio={16 / 10}
								className="aspect-[16/10] w-full object-cover"
								layout="fullWidth"
								priority
								src={imageSrc}
							/>
						) : (
							<div className="aspect-[16/10] bg-[linear-gradient(135deg,#101010_0,#101010_1px,transparent_1px,transparent_12px)] bg-[length:12px_12px]" />
						)}
						{stats?.length ? (
							<div className="border-x border-b border-black bg-black text-white">
								<div className="grid sm:grid-cols-3">
									{stats.map((stat) => (
										<div
											className="border-white/18 border-t p-5 sm:border-r sm:last:border-r-0"
											key={stat.label}
										>
											<p className="text-3xl font-medium">{stat.value}</p>
											<p className="text-white/58 mt-1 text-xs uppercase tracking-[0.16em]">
												{stat.label}
											</p>
										</div>
									))}
								</div>
								{sources?.length ? (
									<p className="border-white/18 text-white/58 border-t px-5 py-3 text-[10px] uppercase tracking-[0.12em]">
										Sources:{' '}
										{sources.map((source, index) => (
											<span key={source.href}>
												{index > 0 ? ' · ' : null}
												<a
													className="text-white underline underline-offset-2"
													href={source.href}
													rel="noreferrer"
													target="_blank"
												>
													{source.label}
												</a>
											</span>
										))}
									</p>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			</div>
		</section>
	)
}

export function MarketingButton({
	children,
	className,
	icon,
	to,
	variant = 'dark',
}: {
	children: ReactNode
	className?: string
	icon?: 'arrow' | 'calendar' | 'chat' | 'search'
	to: string
	variant?: 'dark' | 'light'
}) {
	const buttonIcon = icon ?? getMarketingButtonIcon(children, to)

	return (
		<Link
			className={cn(
				variant === 'dark' ? 'dpe-live-command' : 'dpe-live-demo',
				className,
			)}
			to={to}
		>
			<AnimatedMarketingButtonLabel>{children}</AnimatedMarketingButtonLabel>
			<MarketingButtonIcon icon={buttonIcon} />
		</Link>
	)
}

function AnimatedMarketingButtonLabel({ children }: { children: ReactNode }) {
	return (
		<span className="dpe-live-button-label">
			<span>{children}</span>
			<span aria-hidden="true">{children}</span>
		</span>
	)
}

function MarketingButtonIcon({
	icon,
}: {
	icon: 'arrow' | 'calendar' | 'chat' | 'search'
}) {
	if (icon === 'arrow') {
		return (
			<span aria-hidden="true" className="dpe-live-arrow">
				<span>↗</span>
				<span>↗</span>
			</span>
		)
	}

	return (
		<span aria-hidden="true" className="dpe-live-timer-icon">
			<span>
				<MarketingIconSvg icon={icon} />
			</span>
			<span>
				<MarketingIconSvg icon={icon} />
			</span>
		</span>
	)
}

function MarketingIconSvg({ icon }: { icon: 'calendar' | 'chat' | 'search' }) {
	if (icon === 'search') {
		return (
			<svg fill="none" viewBox="0 0 24 24">
				<circle cx="11" cy="11" r="7" />
				<path d="m16 16 4 4" />
			</svg>
		)
	}

	if (icon === 'chat') {
		return (
			<svg fill="none" viewBox="0 0 24 24">
				<path d="M21 12a8 8 0 0 1-8 8H7l-4 2 2-4a8 8 0 1 1 16-6Z" />
			</svg>
		)
	}

	return (
		<svg fill="none" viewBox="0 0 24 24">
			<path d="M8 2v4" />
			<path d="M16 2v4" />
			<path d="M4 9h16" />
			<rect height="18" rx="2" width="16" x="4" y="4" />
		</svg>
	)
}

function getMarketingButtonIcon(children: ReactNode, to: string) {
	const label =
		typeof children === 'string' ? children.toLowerCase() : String(children)
	const href = to.toLowerCase()

	if (
		label.includes('seo') ||
		label.includes('audit') ||
		href.includes('seo')
	) {
		return 'search'
	}

	if (
		label.includes('demo') ||
		label.includes('schedule') ||
		label.includes('book')
	) {
		return 'calendar'
	}

	if (
		label.includes('contact') ||
		label.includes('support') ||
		label.includes('sales') ||
		label.includes('talk')
	) {
		return 'chat'
	}

	return 'arrow'
}

export function MarketingSection({
	children,
	className,
	eyebrow,
	id,
	title,
}: {
	children: ReactNode
	className?: string
	eyebrow?: string
	id?: string
	title?: string
}) {
	return (
		<section
			className={cn('relative border-b border-black/10', className)}
			id={id}
		>
			<div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-10 lg:py-24">
				{eyebrow || title ? (
					<div className="mb-10 max-w-4xl">
						{eyebrow ? (
							<span className="inline-block border border-black bg-[#EC1C23] px-3 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white">
								{eyebrow}
							</span>
						) : null}
						{title ? (
							<h2 className="mt-4 text-[clamp(2.2rem,4.8vw,5.4rem)] font-medium leading-[0.95] tracking-[-0.03em]">
								{title}
							</h2>
						) : null}
					</div>
				) : null}
				{children}
			</div>
		</section>
	)
}

export function FeatureGrid({ features }: { features: MarketingFeature[] }) {
	return (
		<div className="grid grid-cols-1 border-l border-t border-black md:grid-cols-2 lg:grid-cols-3">
			{features.map((feature) => (
				<div
					className="min-h-[260px] border-b border-r border-black bg-[#fffffa]/60 p-7"
					key={feature.title}
				>
					{feature.label ? (
						<p className="mb-10 text-xs font-semibold uppercase tracking-[0.22em] text-[#EC1C23]">
							{feature.label}
						</p>
					) : null}
					<h3 className="text-2xl font-medium leading-tight">
						{feature.title}
					</h3>
					<p className="text-black/62 mt-5 text-base leading-7">
						{feature.description}
					</p>
				</div>
			))}
		</div>
	)
}

type SplitProofMedia =
	| {
			imageAlt: string
			imageSrc: string
			media?: never
	  }
	| {
			imageAlt?: never
			imageSrc?: never
			media: ReactElement
	  }

type SplitProofProps = {
	body: ReactNode
	kicker: string
	title: string
	unframedMedia?: boolean
} & SplitProofMedia

export function SplitProof(props: SplitProofProps) {
	const { body, kicker, title, unframedMedia } = props

	return (
		<div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
			<div className="lg:col-span-5">
				<span className="inline-block border border-black bg-[#EC1C23] px-3 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white">
					{kicker}
				</span>
				<h2 className="mt-5 text-[clamp(2.2rem,4vw,4.6rem)] font-medium leading-[0.98] tracking-[-0.03em]">
					{title}
				</h2>
				<div className="text-black/64 mt-7 space-y-5 text-lg leading-8">
					{body}
				</div>
			</div>
			<div className="lg:col-span-7">
				<div
					className={cn(
						!unframedMedia && 'border border-black bg-[#fffffa] p-3',
					)}
				>
					{props.media !== undefined ? (
						props.media
					) : (
						<MediaImage
							alt={props.imageAlt}
							aspectRatio={16 / 10}
							className="aspect-[16/10] w-full object-contain"
							fit="contain"
							layout="fullWidth"
							src={props.imageSrc}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

export function GridBackdrop() {
	return (
		<div
			aria-hidden="true"
			className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(16,16,16,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,16,16,0.08)_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]"
		/>
	)
}
